import { useState } from 'react'
import { useSessao } from '../../../auth/contexto'
import { podeFiscalizar } from '../../../auth/permissoes'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro } from '../../../components/Estados'
import { SomenteConsulta } from '../../../components/SomenteConsulta'
import { CampoDeTexto } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import { paraNumero } from '../../../lib/dinheiro'
import { useRegistrarInfraestrutura } from '../../../api/queries/fiscalizacaoManual'
import { DisponibilidadeSondada } from './DisponibilidadeSondada'
import type { DisponibilidadeSondada as Sondagem } from '../../../types/chamado'

/** Os pares de números que cada sub-indicador pede, com o nome que o contrato lhes dá. */
const CAMPOS = [
  { chave: 'backupsComSucesso', rotulo: 'Backups concluídos com sucesso' },
  { chave: 'backupsExecutados', rotulo: 'Backups executados no período' },
  { chave: 'restoresComSucesso', rotulo: 'Restaurações concluídas com sucesso' },
  { chave: 'restoresSolicitados', rotulo: 'Restaurações solicitadas no período' },
  { chave: 'minutosDoPeriodo', rotulo: 'Minutos do mês (total bruto)' },
  { chave: 'minutosAcordados', rotulo: 'Minutos de parada acordada' },
  { chave: 'minutosDeDowntime', rotulo: 'Minutos de indisponibilidade' },
  { chave: 'mensagensEnviadas', rotulo: 'Mensagens de incidente enviadas' },
  { chave: 'mensagensDevidas', rotulo: 'Mensagens que deveriam ter sido enviadas' },
] as const

type Chave = (typeof CAMPOS)[number]['chave']

/**
 * Os pares que cada sub-indicador exige. Um par completo é o mínimo para haver número.
 *
 * A disponibilidade pede TRÊS, e não dois: o "tempo total" da fórmula é líquido das paradas
 * acordadas (TR p.164-165), então sem `minutosAcordados` não há denominador. Zero ali é resposta
 * legítima — "nenhuma parada acordada foi informada" —, mas em branco não é zero.
 */
const PARES: Chave[][] = [
  ['backupsComSucesso', 'backupsExecutados'],
  ['restoresComSucesso', 'restoresSolicitados'],
  ['minutosDoPeriodo', 'minutosAcordados', 'minutosDeDowntime'],
  ['mensagensEnviadas', 'mensagensDevidas'],
]

/**
 * O registro da medição de infraestrutura do mês — os quatro sub-indicadores do IIE.
 *
 * **Pede os números brutos, não os percentuais.** 24 backups com sucesso em 31 executados, e não
 * 77%. É a fração que se confere contra o log da ferramenta, e é o arredondamento — que acontece
 * depois — que decide a faixa: guardar o percentual já arredondado jogaria fora o dígito.
 *
 * **Campo em branco não é zero.** Um mês em que ninguém pediu restauração não tem taxa de
 * restauração; mandar zero ali afirmaria que todas falharam, e zero cai na pior faixa da tabela.
 *
 * @param props o mês em apuração
 * @returns o formulário
 */
export function MedicaoDeInfraestrutura({ competencia, sondagem }: { competencia: string; sondagem: Sondagem | null }) {
  const t = TEXTOS.iie
  const { usuario } = useSessao()
  const registrar = useRegistrarInfraestrutura()
  const [valores, setValores] = useState<Partial<Record<Chave, string>>>({})
  const [fonte, setFonte] = useState('')
  const [adotada, setAdotada] = useState(false)

  /**
   * Adota o que a sonda mediu.
   *
   * Preenche `minutosAcordados` com zero, e não em branco: a sonda não distingue parada acordada de
   * parada não acordada — ela só sabe se houve resposta. Deixar o campo vazio faria o CHECK do banco
   * recusar a trinca, e preenchê-lo com zero declara o que de fato se sabe: nenhuma parada acordada
   * foi informada. O fiscal corrige se souber de alguma.
   */
  function adotarSondagem(minutosDoPeriodo: number, minutosDeDowntime: number) {
    setValores((atuais) => ({
      ...atuais,
      minutosDoPeriodo: String(minutosDoPeriodo),
      minutosAcordados: '0',
      minutosDeDowntime: String(minutosDeDowntime),
    }))
    setFonte((atual) => atual.trim() || (sondagem?.fonte ?? ''))
    setAdotada(true)
  }

  const { numeros, quebrados } = lerOsNove(valores)
  // O botão disparava só com a fonte preenchida, e o CHECK do banco aceita os nove nulos de
  // propósito (par ausente é legítimo; os quatro ausentes não são). Um clique queimava a
  // competência: `iie_medicao` é append-only por gatilho, então a medição vazia só sai por
  // revogação com motivo. O servidor recusa isto também — o `curl` não passa pela tela.
  const algumParCompleto = PARES.some((par) => par.every((chave) => (valores[chave] ?? '').trim().length > 0))
  // A cobrança só aparece depois que a pessoa começou a preencher. Uma tarja âmbar `role="alert"`
  // sobre um formulário intocado é uma repreensão antes de qualquer ato — a mesma razão pela qual
  // o `DialogoDeTexto` só avisa do texto curto depois da primeira tecla.
  const tocou = fonte.trim().length > 0 || CAMPOS.some(({ chave }) => (valores[chave] ?? '').trim().length > 0)

  function enviar() {
    registrar.mutate({ competencia, ...numeros, fonte: fonte.trim() } as never)
  }

  // Registrar a medição é `@PreAuthorize(Autorizacoes.FISCAL)`. Quem só consulta continua sabendo
  // que a medição do mês falta — é isso que explica o IIE sem número — e não recebe nove campos
  // cujo envio termina em 403. O `useState` acima roda antes desta saída: hook não se pula.
  if (!podeFiscalizar(usuario)) {
    return <SomenteConsulta titulo={t.semRegistro.titulo} ajuda={t.semRegistro.ajuda} />
  }

  return (
    <section className="cartao space-y-3 p-4" aria-label={t.semRegistro.titulo}>
      <h2 className="text-base font-semibold text-slate-900">{t.semRegistro.titulo}</h2>
      <p className="max-w-prose text-sm text-slate-600">{t.semRegistro.ajuda}</p>

      {sondagem && (
        <DisponibilidadeSondada sondagem={sondagem} aoAdotar={adotarSondagem} jaAdotada={adotada} />
      )}
      <p className="max-w-prose text-xs text-slate-600">{t.ajudaDosNumeros}</p>

      <div className="grid gap-3 sm:grid-cols-2">
        {CAMPOS.map(({ chave, rotulo }) => (
          <CampoDeTexto
            key={chave}
            rotulo={rotulo}
            inputMode="numeric"
            value={valores[chave] ?? ''}
            onChange={(e) => setValores((atuais) => ({ ...atuais, [chave]: e.target.value }))}
          />
        ))}
      </div>

      <CampoDeTexto
        rotulo="De onde os números foram lidos"
        ajuda="A ferramenta: Duplicati, Zabbix, PRTG, o relatório mensal de infraestrutura."
        value={fonte}
        onChange={(e) => setFonte(e.target.value)}
      />

      {quebrados.length > 0 && (
        <p className="max-w-prose rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900" role="alert">
          {t.numerosQuebrados(quebrados.join(', '))}
        </p>
      )}

      {/* A razão à vista, e não um botão morto: quem preencheu só a fonte precisa saber o que
          falta, e por que o sistema não aceita gravar assim. */}
      {quebrados.length === 0 && tocou && !algumParCompleto && (
        <p className="max-w-prose rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900" role="alert">
          {t.semNenhumPar}
        </p>
      )}

      {registrar.isError && <AvisoDeErro erro={registrar.error} />}

      <Botao
        aparencia="primario"
        disabled={fonte.trim().length === 0 || quebrados.length > 0 || !algumParCompleto || registrar.isPending}
        onClick={enviar}
      >
        {registrar.isPending ? t.registrando : t.registrar}
      </Botao>
    </section>
  )
}

/**
 * Lê os nove campos como pt-BR os escreve, e nomeia os que não viraram contagem inteira.
 *
 * **O que havia era `Number(bruto)`, e ele lia "1.200" como 1,2.** Cenário: a contratada informa
 * 1.200 backups executados e 1.020 com sucesso; a tela mandava 1,2 e 1,02; `InfraestruturaRequest`
 * declara `Integer` e o Jackson, com `ACCEPT_FLOAT_AS_INT` ligado por padrão, **truncava para 1 e
 * 1**. O CHECK `backups_com_sucesso <= backups_executados` passava com 1 ≤ 1, e o sub-indicador
 * saía 100% onde a realidade era 85% — a faixa "80% a 89%" desaparecia com seus 5 pontos
 * percentuais de redutor, a favor da contratada, sem nada na tela denunciar. `44.640` minutos do
 * mês viravam 44.
 *
 * Os nove são contagens ou minutos: todo valor não inteiro é erro de digitação, e ele é dito em
 * voz alta em vez de silenciosamente arredondado. Campo em branco continua sendo `null`, que não é
 * zero — a distinção do cabeçalho deste arquivo.
 *
 * @param valores o que está digitado em cada campo
 * @returns os números prontos para o POST e os rótulos dos campos ilegíveis
 */
function lerOsNove(valores: Partial<Record<Chave, string>>) {
  const numeros: Record<string, number | null> = {}
  const quebrados: string[] = []
  for (const { chave, rotulo } of CAMPOS) {
    const bruto = valores[chave]?.trim()
    if (!bruto) {
      numeros[chave] = null
      continue
    }
    const numero = paraNumero(bruto)
    if (!Number.isInteger(numero) || numero < 0) {
      quebrados.push(rotulo)
      continue
    }
    numeros[chave] = numero
  }
  return { numeros, quebrados }
}
